/*
 * "↻ Again from Sat 12 Sep" (SF 41, 41b, N13): what a pet had at its last
 * visit to this location, matched to today's menu. Pure; the services step
 * passes in the client's bookings and the menu.
 */
import { asUtc } from './bookedSummary';
import type { MenuCategory, MenuOption, MenuService } from './serviceMenu';

export interface VisitLine {
  serviceId?: number | string | null;
  pricingOptionId?: number | string | null;
  serviceName?: string | null;
}

export interface VisitLike {
  storeId?: number | null;
  /** When it started (UTC ISO). */
  startUtc?: string | null;
  /** The API's status number. */
  status?: number | null;
  /** The visit's lines, by pet id. */
  servicesByPet?: Record<string, VisitLine[] | undefined>;
}

/**
 * A visit that happened: Confirmed (1), Checked in (2), Ready (3) or
 * Completed (4), and started before now. A no-show (5), a cancellation
 * (6), and a booking that was only ever held or lapsed unpaid (7, 8) are
 * not a last visit.
 */
const HAPPENED = new Set([1, 2, 3, 4]);

export interface LastVisit {
  whenUtc: string;
  lines: VisitLine[];
}

const listOf = (v: unknown): any[] => (Array.isArray(v) ? v : []);

/**
 * The visits in the CMS's answer for the client's own bookings
 * (`/api/appointment/by-client`: `activeAppointments`, then
 * `archivedAppointments`), as the last visit needs them: where, when, the
 * status, and each pet's lines by option (the service too, where the
 * booking names it). A booking that is live in the API names the pet and the
 * option by id; an archived one embeds them. Nothing else is read, so the
 * services step no longer waits for the client record, the locations and
 * every menu the old transform (My bookings') read after it.
 */
export const visitsFromBookings = (body: unknown): VisitLike[] => {
  const b = (body ?? {}) as { activeAppointments?: unknown; archivedAppointments?: unknown };
  return [...listOf(b.activeAppointments), ...listOf(b.archivedAppointments)].map(a => {
    const servicesByPet: Record<string, VisitLine[]> = {};
    for (const s of listOf(a?.appointmentPetServices)) {
      const petId = s?.petId ?? s?.pet?.id;
      if (petId == null) continue;
      const option = s?.groomingServicePricingOption;
      const line: VisitLine = {
        serviceId: s?.groomingServiceId ?? option?.groomingServiceId ?? option?.groomingService?.id ?? null,
        pricingOptionId: s?.groomingServicePricingOptionId ?? option?.id ?? null,
        serviceName: option?.groomingService?.name ?? null
      };
      (servicesByPet[String(petId)] ??= []).push(line);
    }
    return {
      storeId: a?.storeId ?? a?.store?.id ?? null,
      startUtc: a?.startTime ?? null,
      status: a?.status ?? null,
      servicesByPet
    };
  });
};

const wholeId = (v: unknown): number | null => (typeof v === 'number' && Number.isInteger(v) && v > 0 ? v : null);

/**
 * The visits in the CMS's last-visit read (`/api/appointment/last-visits`,
 * CMS G21, mpgadmin G117): per pet, its latest visits at the location asked
 * for, already only those that happened, each `{ petId, startedAt, status,
 * lines: [{ serviceId, optionId }] }`. Each becomes a visit of that one pet,
 * in the shape `visitsFromBookings` gives, so `lastVisitFor` and everything
 * else that reads visits read them the same. Null when the answer isn't a
 * list (the caller then reads the bookings instead); an entry missing its
 * pet, time or lines is left out.
 */
export const visitsFromLastVisits = (body: unknown, storeId: number): VisitLike[] | null => {
  if (!Array.isArray(body)) return null;
  const visits: VisitLike[] = [];
  for (const e of body) {
    const petId = wholeId(e?.petId);
    const startUtc = typeof e?.startedAt === 'string' ? e.startedAt : null;
    if (petId === null || startUtc === null || !Array.isArray(e?.lines)) continue;
    const lines: VisitLine[] = e.lines
      .filter((l: unknown) => l && typeof l === 'object')
      .map((l: { serviceId?: unknown; optionId?: unknown }) => ({
        serviceId: wholeId(l.serviceId),
        pricingOptionId: wholeId(l.optionId),
        serviceName: null
      }));
    if (lines.length === 0) continue;
    visits.push({
      storeId,
      startUtc,
      status: typeof e?.status === 'number' ? e.status : null,
      servicesByPet: { [String(petId)]: lines }
    });
  }
  return visits;
};

/** The pet's latest visit at this location, or null. */
export const lastVisitFor = (
  petId: number | string,
  visits: VisitLike[],
  storeId: number | null | undefined,
  nowMs: number
): LastVisit | null => {
  let best: LastVisit | null = null;
  let bestMs = -Infinity;
  for (const v of visits) {
    if (storeId != null && v.storeId !== storeId) continue;
    if (v.status == null || !HAPPENED.has(v.status)) continue;
    const ms = v.startUtc ? Date.parse(asUtc(v.startUtc)) : NaN;
    if (!Number.isFinite(ms) || ms >= nowMs) continue;
    const lines = v.servicesByPet?.[String(petId)] ?? [];
    if (lines.length === 0) continue;
    if (ms > bestMs) {
      best = { whenUtc: asUtc(v.startUtc as string), lines };
      bestMs = ms;
    }
  }
  return best;
};

/** A category whose services are S. */
export type CategoryOf<S extends MenuService> = Omit<MenuCategory, 'groomingServices'> & { groomingServices?: S[] };

export interface MenuPick<S extends MenuService = MenuService, O extends MenuOption = MenuOption> {
  category: CategoryOf<S>;
  service: S;
  option: O;
}

const idOf = (v: number | string | null | undefined) => (v == null || v === '' ? null : Number(v));

/**
 * The last visit's lines on today's menu: by the option booked, else (the
 * salon has since replaced the option) by the service, at the option the pet
 * would get now. A service no longer on the menu drops out; each service
 * once.
 */
export const picksFromVisit = <S extends MenuService>(
  lines: VisitLine[],
  categories: Array<CategoryOf<S>>,
  optionFor: (service: S) => MenuOption | null
): Array<MenuPick<S>> => {
  const picks: Array<MenuPick<S>> = [];
  const seen = new Set<number>();
  for (const line of lines) {
    const optionId = idOf(line.pricingOptionId);
    const serviceId = idOf(line.serviceId);
    let found: MenuPick<S> | null = null;
    for (const category of categories) {
      for (const service of category.groomingServices ?? []) {
        const option =
          optionId != null ? (service.groomingServicePricingOptions ?? []).find(o => o.id === optionId) : undefined;
        if (option) {
          found = { category, service, option };
          break;
        }
      }
      if (found) break;
    }
    if (!found && serviceId != null) {
      for (const category of categories) {
        const service = (category.groomingServices ?? []).find(s => s.id === serviceId);
        const option = service ? optionFor(service) : null;
        if (service && option) {
          found = { category, service, option };
          break;
        }
      }
    }
    if (found && !seen.has(found.service.id)) {
      seen.add(found.service.id);
      picks.push(found);
    }
  }
  return picks;
};
